import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer, SuiLabel } from '@splashthat/sui';
import FontPickerEditor from 'common/components/FontPicker';
import ColorPickerEditor from 'common/components/ColorPicker';
import AlignmentEditor from 'common/components/AlignmentEditor';

import { getColors } from 'common/entities/color/selectors';
import { getFonts } from 'common/entities/font/selectors';
import { getFormContentStyles } from 'common/entities/form/styleConfiguration/selectors';
import {
    setFormTextElementColor,
    setFormTextElementFont,
    setFormTextElementFontsize,
    setFormTextElementPosition,
} from 'common/entities/form/styleConfiguration/actions';
import { LEFT, CENTER, RIGHT } from 'common/entities/form/styleConfiguration/constants';
import {
    DEFAULT_FONT,
    DEFAULT_FONT_SIZE,
    DEFAULT_HEADLINE_COLOR,
    DEFAULT_TEXT_ELEMENT_POSITION,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';
import { getFormId } from '../../../selectors';
import { TEXT_ELEMENT_FONT_LABEL, TEXT_ELEMENT_ALIGNMENT_LABEL } from '../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../constants/reusableClassNames';

import { TEXT_ELEMENT_QA, FONT_LABEL_QA } from '../../../utils/qaIdConstants';

const baseClassName = 'FormStyleEditorTextElementPanel';

const FormStyleEditorTextElementPanel = ({ formId, colorList, fontList, ...props }) => (
    <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${TEXT_ELEMENT_QA}--panel`}>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${TEXT_ELEMENT_QA}--section-${FONT_LABEL_QA}`}>
            <SuiLabel text={TEXT_ELEMENT_FONT_LABEL} data-qa={`${TEXT_ELEMENT_QA}--label`} />
            <FontPickerEditor
                fontFamilyList={fontList}
                defaultFontFamily={props.font}
                onChangeFontFamily={(id) => props.setFormTextElementFont(formId, id)}
                defaultFontSize={props.fontSize}
                onChangeFontSize={(size) => props.setFormTextElementFontsize(formId, size)}
                fontFamilyQa={`${TEXT_ELEMENT_QA}--fontFamily`}
                fontSizeQa={`${TEXT_ELEMENT_QA}--fontSize`}
            />
            <ColorPickerEditor
                colorList={colorList}
                firstColor={props.color}
                changeFirstColor={(id) => props.setFormTextElementColor(formId, id)}
                qaId={TEXT_ELEMENT_QA}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${TEXT_ELEMENT_QA}--section-alignment`}>
            <SuiContainer className={`${baseClassName}__AlignmentEditor`}>
                <AlignmentEditor
                    label={TEXT_ELEMENT_ALIGNMENT_LABEL}
                    position={props.position}
                    onChangePosition={(position) => props.setFormTextElementPosition(formId, position)}
                    qaId={`${TEXT_ELEMENT_QA}--alignment`}
                />
            </SuiContainer>
        </SuiContainer>
    </SuiContainer>
);

FormStyleEditorTextElementPanel.propTypes = {
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    fontList: PropTypes.arrayOf(PropTypes.object).isRequired,
    formId: PropTypes.string.isRequired,
    color: styleShapePropTypes,
    font: styleShapePropTypes,
    fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    position: PropTypes.oneOf([LEFT, CENTER, RIGHT]),
    setFormTextElementFont: PropTypes.func.isRequired,
    setFormTextElementFontsize: PropTypes.func.isRequired,
    setFormTextElementColor: PropTypes.func.isRequired,
    setFormTextElementPosition: PropTypes.func.isRequired,
};

FormStyleEditorTextElementPanel.defaultProps = {
    color: getDefaultStyle(DEFAULT_HEADLINE_COLOR),
    font: getDefaultStyle(DEFAULT_FONT),
    fontSize: DEFAULT_FONT_SIZE,
    position: DEFAULT_TEXT_ELEMENT_POSITION,
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const fontList = getFonts(state);

    const formContentStyles = getFormContentStyles(state, formId);

    return {
        colorList,
        fontList,
        formId,
        ...formContentStyles,
    };
};

const mapDispatchToProps = {
    setFormTextElementColor,
    setFormTextElementFont,
    setFormTextElementFontsize,
    setFormTextElementPosition,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleEditorTextElementPanel);
